'use client'

import { useEffect } from 'react'
import { useRouter } from 'next/navigation'

/**
 * The keyboard layer: the table, the modifier rule, and the guard.
 *
 * All three arrived from `app/app/layout-client.tsx` on #316 — the route map at `:22-27`, the
 * editable-target guard at `:43-50`, and the keydown effect at `:75-111`. A route-tier file owned a
 * third of this domain, and none of it could be tested without mounting the whole protected layout.
 *
 * **Three of the four bindings act on another domain**, so they arrive as handlers rather than as
 * imports: Cmd+K is `search/`'s, Cmd+N is `capture/`'s, and Cmd+backslash acts on the sidebar state
 * the caller already holds. Only the route map is navigation's own, and only it reaches the router
 * here. That is the split #365 left open, decided this way because the alternative — this module
 * importing `@/search` and `@/capture` — would put two domain faces into the initialisation path of
 * every `/app` page, which is the shape `feed/row-chrome/index.ts` and the `social/` zone both
 * record as a cycle hazard.
 *
 * The table is moved as it stands. [#189](https://github.com/cinjoff/konstantout/issues/189)
 * rewrites it into one declaration naming every binding, its action and its label, and #185 and
 * #186 are still open on what the bindings should be; landing that here would have made the move
 * and the rewrite one diff.
 */

/** Cmd+1..4. `SHORTCUT_ROUTES` is keyed by `KeyboardEvent.key`, so the keys are strings. */
export const SHORTCUT_ROUTES: Record<string, string> = {
  '1': '/app',
  '2': '/app/upcoming',
  '3': '/app/projects',
  '4': '/app/journal',
}

/** Whether a shortcut should yield to the element the event landed on. */
function isEditableTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null
  return (
    el instanceof HTMLInputElement ||
    el instanceof HTMLTextAreaElement ||
    el?.isContentEditable === true
  )
}

export interface NavigationShortcutHandlers {
  onSearchOpen: () => void
  onCaptureOpen: () => void
  onToggleSidebar: () => void
}

export function useNavigationShortcuts({
  onSearchOpen,
  onCaptureOpen,
  onToggleSidebar,
}: NavigationShortcutHandlers): void {
  const router = useRouter()

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (isEditableTarget(e.target)) return
      if (!(e.metaKey || e.ctrlKey)) return

      // Cmd+K — search
      if (e.key === 'k') {
        e.preventDefault()
        onSearchOpen()
        return
      }

      // Cmd+\ — toggle sidebar
      if (e.key === '\\') {
        e.preventDefault()
        onToggleSidebar()
        return
      }

      // Cmd+N — capture
      if (e.key === 'n') {
        e.preventDefault()
        onCaptureOpen()
        return
      }

      // Cmd+1..4 — navigate
      const route = SHORTCUT_ROUTES[e.key]
      if (route) {
        e.preventDefault()
        router.push(route)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onSearchOpen, onCaptureOpen, onToggleSidebar, router])
}
